import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useAccount } from 'wagmi';
import ConnectWalletButton from '../components/ConnectWalletButton';
import { useNftVerification } from '../hooks/useNftVerification';
import { useSignatureVerification } from '../hooks/useSignatureVerification';
import axios from 'axios';
import { VERIFY_NFT_URL, ROLE_STATUS_URL, ASSIGN_ROLE_URL } from '../api';

// Create axios instance with consistent configuration
const roleAxios = axios.create({
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  }
});

const Dashboard = () => {
  const { user, logout } = useAuth();
  const { address, isConnected } = useAccount();
  const [walletConnected, setWalletConnected] = useState(false);
  const [signatureVerified, setSignatureVerified] = useState(false);
  const [nftVerified, setNftVerified] = useState(false);
  const [roleAssigned, setRoleAssigned] = useState(false);
  const [isAssigningRole, setIsAssigningRole] = useState(false);
  const [error, setError] = useState(null);
  
  // NFT verification hook
  const { 
    verifyNft, 
    isVerifying: isVerifyingNft, 
    isVerified: isNftVerified, 
    error: nftError,
    hasNft
  } = useNftVerification();
  
  // Signature verification hook
  const {
    verifySignature,
    isVerifying: isVerifyingSignature,
    isVerified: isSignatureVerified,
    error: signatureError
  } = useSignatureVerification();
  
  // Update wallet connected state based on wagmi hook
  useEffect(() => {
    setWalletConnected(isConnected && !!address);
  }, [isConnected, address]);
  
  // Update signature verified state from hook
  useEffect(() => {
    setSignatureVerified(isSignatureVerified);
  }, [isSignatureVerified]);
  
  // Update NFT verified state
  useEffect(() => {
    if (signatureVerified && hasNft) {
      setNftVerified(true);
    }
  }, [signatureVerified, hasNft]);
  
  // Check if the user already has the role
  useEffect(() => {
    const checkRoleStatus = async () => {
      if (!user) return;
      
      try {
        console.log('Checking role status...');
        const response = await roleAxios.get(ROLE_STATUS_URL);
        console.log('Role status response:', response.data);
        
        if (response.data.hasRole) {
          setRoleAssigned(true);
        }
      } catch (error) {
        console.error('Failed to check role status:', error);
      }
    };
    
    checkRoleStatus();
  }, [user]);
  
  // Handle wallet connection
  const handleConnect = (address) => {
    console.log('Wallet connected:', address);
    setWalletConnected(true);
    setError(null);
  };
  
  // Handle wallet disconnection
  const handleDisconnect = () => {
    setWalletConnected(false);
    setSignatureVerified(false);
    setNftVerified(false);
  };
  
  // Handle signature verification
  const handleVerifySignature = async () => {
    setError(null);
    
    try {
      console.log('Starting signature verification process');
      const success = await verifySignature();
      console.log('Signature verification result:', success);
      
      if (success) {
        setSignatureVerified(true);
        setNftVerified(false); // Reset NFT verification status
        // If we already know they have the NFT, automatically set it as verified
        console.log('Checking NFT verification status...');
        const nft = await verifyNft();
        if (nft) {
          console.log('NFT already verified, setting status');
          setNftVerified(true);
        } else if (nftError) {
          console.error('NFT verification error:', nftError);
          setError(nftError);
        }else{
          console.error('NFT', nft);
        }
      } else if (signatureError) {
        setError(signatureError);
      }
    } catch (err) {
      console.error('Error during signature verification:', err);
      setError('Failed to verify signature: ' + (err.message || 'Unknown error'));
    }
  };
  
  // Handle NFT verification
  const handleVerifyNft = async () => {
    if (!signatureVerified) {
      setError('Please verify your wallet ownership first');
      return;
    }
    
    setError(null);
    
    if (hasNft) {
      setNftVerified(true);
      return true;
    }
    
    const success = await verifyNft();
    if (success) {
      setNftVerified(true);
    } else if (nftError) {
      setError(nftError);
    }
  };
  
  // Handle the role assignment
  const handleRoleAssignment = async () => {
    if (!nftVerified || !signatureVerified) {
      setError('Complete the previous steps first');
      return;
    }
    
    setIsAssigningRole(true);
    setError(null);
    
    try {
      console.log('Assigning role...');
      const response = await roleAxios.post(ASSIGN_ROLE_URL, {
        walletAddress: address
      });
      
      console.log('Role assignment response:', response.data);
      
      if (response.data.success) {
        setRoleAssigned(true);
        setError(null);
      } else {
        setError(response.data.message || 'Failed to assign role');
      }
    } catch (err) {
      console.error('Role assignment failed:', err);
      setError(err.response?.data?.message || 'Role assignment failed');
    } finally {
      setIsAssigningRole(false);
    }
  };
  
  return (
    <div className="dashboard-container">
      <div className="user-info">
        <h1>Welcome,{user?.username}</h1>
        {user?.avatar && (
          <img 
            src={`https://cdn.discordapp.com/avatars/${user.id}/${user.avatar}.png`} 
            alt={`${user.username}'s avatar`}
            className="avatar"
          />
        )}
      </div>
      
      <div className="verification-steps">
        <div className="step">
          <h3>Step 1: Discord Login</h3>
          <p className="status-success">✅ Completed</p>
        </div>
        
        <div className="step">
          <h3>Step 2: Connect Wallet</h3>
          <ConnectWalletButton 
            onConnect={handleConnect} 
            onDisconnect={handleDisconnect} 
          />
          {walletConnected && <p className="status-success">✅ Wallet Connected</p>}
        </div>
        
        {walletConnected && (
          <div className="step">
            <h3>Step 3: Verify Wallet Ownership</h3>
            {!signatureVerified ? (
              <>
                <button 
                  className="verify-signature-btn" 
                  onClick={handleVerifySignature}
                  disabled={isVerifyingSignature}
                >
                  {isVerifyingSignature ? 'Verifying...' : 'Sign Message'}
                </button>
                {error && !signatureError && <p className="error-message">{error}</p>}
                {signatureError && <p className="error-message">{signatureError}</p>}
              </>
            ) : (
              <>
                <p className="status-success">✅ Wallet Ownership Verified</p>

                {!nftVerified ? (
                  <>
                    {error && !nftError && <p className="error-message">{error}</p>}
                    {nftError && <p className="error-message">{nftError}</p>}
                  </>
                ) : (
                  <p className="status-success">✅ NFT Verified</p>
                )}
              </>
            )}
          </div>
        )}
        
        {nftVerified && (
          <div className="step">
            <h3>Step 4: Claim Discord Role</h3>
            {!roleAssigned ? (
              <>
                <button 
                  className="assign-role-btn" 
                  onClick={handleRoleAssignment}
                  disabled={isAssigningRole}
                >
                  {isAssigningRole ? 'Assigning...' : 'Claim Role'}
                </button>
                {error && <p className="error-message">{error}</p>}
              </>
            ) : (
              <p className="status-success">✅ Role Assigned</p>
            )}
          </div>
        )}
      </div>
      
      <button className="logout-btn" onClick={logout}>
        Logout
      </button>
    </div>
  );
};

export default Dashboard; 